import { IconAddCircleOutline, IconEdit, IconExpandMore, IconFileDownload, IconSave } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { ConfirmLeavePage } from "@/components/commons/ConfirmLeavePage";
import { InnerBox } from "@/components/elements/InnerBox";
import { Select } from "@/components/elements/Select";
import { TopPage } from "@/components/elements/TopPage";
import { LedgerTable } from "@/features/management-ledger/components/LedgerTable";
import { LedgerTabs } from "@/features/management-ledger/components/LedgerTabs";
import { getFeeLedgerColumns, TUpdateFeeLedgerRow } from "@/features/management-ledger/constants/fee-ledger-columns";
import { getJobLedgerColumns, TEmploymentPeriodDrafts, TLedgerDraftEdits, TUpdateLedgerDraft } from "@/features/management-ledger/constants/job-ledger-columns";
import { TUpdateEmploymentPeriodDraft } from "@/features/management-ledger/components/LedgerEmploymentPeriodEditableCell";
import { isLedgerNoteValueValid } from "@/features/management-ledger/components/LedgerNoteEditableCell";
import { isFeeAmountInvalid, isFeeDateInvalid } from "@/features/management-ledger/components/FeeLedgerEditableCells";
import { isBanPeriodValid } from "@/features/management-ledger/utils/job-change-ban-period";
import { getJobseekerLedgerColumns, TValidPeriodDrafts } from "@/features/management-ledger/constants/jobseeker-ledger-columns";
import { TUpdateValidPeriodDraft } from "@/features/management-ledger/components/LedgerValidPeriodEditableCell";
import { LEDGER_TAB, LEDGER_TAB_OPTIONS, TLedgerTabValue } from "@/features/management-ledger/constants/ledger-tabs";
import { TFeeLedgerRow } from "@/features/management-ledger/types/management-ledger";
import { exportFeeLedgerApi, useFeeLedgerList, useFeePayerOptions, useSaveFeeLedger } from "@/api/services/main/fee-ledger";
import { exportJobLedgerApi, useFlushJobLedgerEmploymentPeriods, useFlushJobLedgerNotes, useJobLedgerList } from "@/api/services/main/job-ledger";
import { exportJobseekerLedgerApi, useFlushJobseekerLedgerNotes, useFlushJobseekerLedgerValidPeriods, useJobseekerLedgerList } from "@/api/services/main/talent-ledger";
import { useEnum } from "@/api/services/global/enum";
import { assignJobRowSpans, transformJobLedgerDtoToRow } from "@/features/management-ledger/utils/transform-job-ledger-dto";
import { assignTalentRowSpans, transformJobseekerLedgerDtoToRow } from "@/features/management-ledger/utils/transform-jobseeker-ledger-dto";
import { isFeeRowDirty, isFeeRowEmpty } from "@/features/management-ledger/utils/fee-ledger-row";
import { Pagination } from "@/components/elements/Pagination";
import { usePagination } from "@/hooks/usePagination";
import { START_PAGE } from "@/constants/pagination";
import { button, icon, text } from "@/theme/colors";
import { API_SUCCESS_MESSAGE, API_UNKNOWN_ERROR_MESSAGE, DOWNLOAD_FAILED_MESSAGE, DOWNLOAD_SUCCESS_MESSAGE, INPUT_MAX_LENGTH_MESSAGE, MAX_CHARACTER_INPUT_VALIDATION, UNSAVED_CHANGES_CONFIRM_MESSAGE } from "@/constants/common";
import { toast } from "@/utils/toast";
import { downloadBlobResponse } from "@/utils/export";
import { TOption } from "@/types/common";
import { Modal } from "@/components/elements/Modal";
import { Box, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
const getCurrentFiscalYear = () => {
    const now = new Date();
    return now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
};
const getFiscalYearOptions = (): TOption[] => {
    const currentFY = getCurrentFiscalYear();
    return Array.from({ length: 5 }, (_, i) => {
        const year = currentFY - 4 + i;
        return { label: `${year}`, value: year };
    });
};
const getActiveLedgerLabel = (tab: TLedgerTabValue) => LEDGER_TAB_OPTIONS.find((opt) => opt.value === tab)?.label ?? "";
let feeRowKeySeq = 0;
const nextFeeRowKey = () => `fee-row-${(feeRowKeySeq += 1)}`;
const createEmptyFeeRow = (): TFeeLedgerRow => ({
    rowKey: nextFeeRowKey(),
    id: null,
    payerCompanyId: null,
    payerName: null,
    collectionDate: null,
    feeType: "紹介手数料",
    feeAmount: "",
    feeBasis: "",
    remarks: "",
});
const isFeeRowOverLimit = (row: TFeeLedgerRow) =>
  row.feeType.length > MAX_CHARACTER_INPUT_VALIDATION ||
  row.feeBasis.length > MAX_CHARACTER_INPUT_VALIDATION ||
  row.remarks.length > MAX_CHARACTER_INPUT_VALIDATION ||
  isFeeAmountInvalid(row.feeAmount) ||
  isFeeDateInvalid(row.collectionDate);
const hasInvalidDraft = (drafts: TLedgerDraftEdits) =>
    Object.values(drafts).some((fields) =>
        Object.entries(fields).some(([field, value]) => {
            const text = value ?? "";
            if (!isLedgerNoteValueValid(text)) return true;
            if (field === "permanentEmploymentNote") return !isBanPeriodValid(text);
            return false;
        }));
export const ManagementLedgerPage = () => {
    const [activeTab, setActiveTab] = useState<TLedgerTabValue>(LEDGER_TAB.job);
    const [fiscalYear, setFiscalYear] = useState<number>(() => getCurrentFiscalYear());
    const yearOptions = useMemo(() => getFiscalYearOptions(), []);
    const [, setSearchParams] = useSearchParams();
    const { page, perPage } = usePagination();
    const handleFiscalYearChange = (value: number) => {
        setFiscalYear(value);
        setSearchParams((prev) => {
            prev.set("page", String(START_PAGE));
            return prev;
        });
    };
    const { data: jobLedgerData, isLoading: isJobLedgerLoading } = useJobLedgerList({ fiscalYear, page, perPage }, activeTab === LEDGER_TAB.job);
    const { data: salaryTypeOptions } = useEnum("ESalaryType");
    const jobLedgerRows = useMemo(() => assignJobRowSpans((jobLedgerData?.data ?? []).map((dto) => transformJobLedgerDtoToRow(dto, salaryTypeOptions))), [jobLedgerData, salaryTypeOptions]);
    const { data: jobseekerLedgerData, isLoading: isJobseekerLedgerLoading } = useJobseekerLedgerList({ fiscalYear, page, perPage }, activeTab === LEDGER_TAB.jobseeker);
    const jobseekerLedgerRows = useMemo(() => assignTalentRowSpans((jobseekerLedgerData?.data ?? []).map(transformJobseekerLedgerDtoToRow)), [jobseekerLedgerData]);
    const [isEditing, setIsEditing] = useState(false);
    const [draftEdits, setDraftEdits] = useState<TLedgerDraftEdits>({});
    const [jobEmploymentPeriodDrafts, setJobEmploymentPeriodDrafts] = useState<TEmploymentPeriodDrafts>({});
    const [referralEmploymentPeriodDrafts, setReferralEmploymentPeriodDrafts] = useState<TEmploymentPeriodDrafts>({});
    const hasDraftEdits = Object.keys(draftEdits).length > 0 || Object.keys(jobEmploymentPeriodDrafts).length > 0 || Object.keys(referralEmploymentPeriodDrafts).length > 0;
    const isJobDraftInvalid = useMemo(() => hasInvalidDraft(draftEdits), [draftEdits]);
    const updateDraft: TUpdateLedgerDraft = useCallback((caseMatchId, field, value) => {
        setDraftEdits((prev) => ({ ...prev, [caseMatchId]: { ...prev[caseMatchId], [field]: value } }));
    }, []);
    const updateJobEmploymentPeriodDraft: TUpdateEmploymentPeriodDraft = useCallback((jobId, value) => {
        setJobEmploymentPeriodDrafts((prev) => ({ ...prev, [jobId]: value }));
    }, []);
    const updateReferralEmploymentPeriodDraft: TUpdateEmploymentPeriodDraft = useCallback((caseMatchId, value) => {
        setReferralEmploymentPeriodDrafts((prev) => ({ ...prev, [caseMatchId]: value }));
    }, []);
    const { mutateAsync: flushLedgerNotes, isPending: isSaving } = useFlushJobLedgerNotes();
    const { mutateAsync: flushJobEmploymentPeriods, isPending: isSavingJobEmploymentPeriods } = useFlushJobLedgerEmploymentPeriods();
    const handleSave = useCallback(async () => {
        if (hasInvalidDraft(draftEdits)) {
            toast.error(INPUT_MAX_LENGTH_MESSAGE(MAX_CHARACTER_INPUT_VALIDATION));
            return;
        }
        const noteCaseMatchIds = new Set([...Object.keys(draftEdits), ...Object.keys(referralEmploymentPeriodDrafts)].map(Number));
        const patches = Array.from(noteCaseMatchIds).map((caseMatchId) => ({
            caseMatchId,
            payload: {
                ...draftEdits[caseMatchId],
                ...(caseMatchId in referralEmploymentPeriodDrafts ? { employmentPeriod: referralEmploymentPeriodDrafts[caseMatchId] } : {}),
            },
        }));
        const employmentPeriodPatches = Object.entries(jobEmploymentPeriodDrafts).map(([jobId, value]) => ({ jobId: Number(jobId), payload: { employmentPeriod: value } }));
        if (!patches.length && !employmentPeriodPatches.length) {
            setIsEditing(false);
            return;
        }
        try {
            await Promise.all([
                patches.length ? flushLedgerNotes(patches) : Promise.resolve(),
                employmentPeriodPatches.length ? flushJobEmploymentPeriods(employmentPeriodPatches) : Promise.resolve(),
            ]);
            setDraftEdits({});
            setJobEmploymentPeriodDrafts({});
            setReferralEmploymentPeriodDrafts({});
            setIsEditing(false);
            toast.success(API_SUCCESS_MESSAGE);
        }
        catch {
            toast.error(API_UNKNOWN_ERROR_MESSAGE);
        }
    }, [draftEdits, jobEmploymentPeriodDrafts, referralEmploymentPeriodDrafts, flushLedgerNotes, flushJobEmploymentPeriods]);
    const jobLedgerColumns = useMemo(() => getJobLedgerColumns(draftEdits, updateDraft, isEditing, jobEmploymentPeriodDrafts, updateJobEmploymentPeriodDraft, referralEmploymentPeriodDrafts, updateReferralEmploymentPeriodDraft), [draftEdits, updateDraft, isEditing, jobEmploymentPeriodDrafts, updateJobEmploymentPeriodDraft, referralEmploymentPeriodDrafts, updateReferralEmploymentPeriodDraft]);
    const [isEditingJobseeker, setIsEditingJobseeker] = useState(false);
    const [showJobseekerValidation, setShowJobseekerValidation] = useState(false);
    const [jobseekerDraftEdits, setJobseekerDraftEdits] = useState<TLedgerDraftEdits>({});
    const [jobseekerEmploymentPeriodDrafts, setJobseekerEmploymentPeriodDrafts] = useState<TEmploymentPeriodDrafts>({});
    const hasJobseekerDraftEdits = Object.keys(jobseekerDraftEdits).length > 0 || Object.keys(jobseekerEmploymentPeriodDrafts).length > 0;
    const [validPeriodDrafts, setValidPeriodDrafts] = useState<TValidPeriodDrafts>({});
    const hasValidPeriodDrafts = Object.keys(validPeriodDrafts).length > 0;
    const isJobseekerDraftInvalid = useMemo(() => hasInvalidDraft(jobseekerDraftEdits) || Object.values(validPeriodDrafts).some(isFeeDateInvalid), [jobseekerDraftEdits, validPeriodDrafts]);
    const updateJobseekerDraft: TUpdateLedgerDraft = useCallback((caseMatchId, field, value) => {
        setJobseekerDraftEdits((prev) => ({ ...prev, [caseMatchId]: { ...prev[caseMatchId], [field]: value } }));
    }, []);
    const updateJobseekerEmploymentPeriodDraft: TUpdateEmploymentPeriodDraft = useCallback((caseMatchId, value) => {
        setJobseekerEmploymentPeriodDrafts((prev) => ({ ...prev, [caseMatchId]: value }));
    }, []);
    const updateValidPeriodDraft: TUpdateValidPeriodDraft = useCallback((talentId, value) => {
        setValidPeriodDrafts((prev) => ({ ...prev, [talentId]: value }));
    }, []);
    const { mutateAsync: flushJobseekerLedgerNotes, isPending: isSavingJobseeker } = useFlushJobseekerLedgerNotes();
    const { mutateAsync: flushJobseekerLedgerValidPeriods, isPending: isSavingValidPeriods } = useFlushJobseekerLedgerValidPeriods();
    const handleSaveJobseeker = useCallback(async () => {
        setShowJobseekerValidation(true);
        if (isJobseekerDraftInvalid) {
            return;
        }
        const noteCaseMatchIds = new Set([...Object.keys(jobseekerDraftEdits), ...Object.keys(jobseekerEmploymentPeriodDrafts)].map(Number));
        const notePatches = Array.from(noteCaseMatchIds).map((caseMatchId) => ({
            caseMatchId,
            payload: {
                ...jobseekerDraftEdits[caseMatchId],
                ...(caseMatchId in jobseekerEmploymentPeriodDrafts ? { employmentPeriod: jobseekerEmploymentPeriodDrafts[caseMatchId] } : {}),
            },
        }));
        const validPeriodPatches = Object.entries(validPeriodDrafts).map(([talentId, value]) => ({
            talentId: Number(talentId),
            payload: { validPeriod: value || null },
        }));
        if (!notePatches.length && !validPeriodPatches.length) {
            setIsEditingJobseeker(false);
            return;
        }
        try {
            await Promise.all([
                notePatches.length ? flushJobseekerLedgerNotes(notePatches) : Promise.resolve(),
                validPeriodPatches.length ? flushJobseekerLedgerValidPeriods(validPeriodPatches) : Promise.resolve(),
            ]);
            setJobseekerDraftEdits({});
            setJobseekerEmploymentPeriodDrafts({});
            setValidPeriodDrafts({});
            setIsEditingJobseeker(false);
            setShowJobseekerValidation(false);
            toast.success(API_SUCCESS_MESSAGE);
        }
        catch {
            toast.error(API_UNKNOWN_ERROR_MESSAGE);
        }
    }, [jobseekerDraftEdits, jobseekerEmploymentPeriodDrafts, validPeriodDrafts, flushJobseekerLedgerNotes, flushJobseekerLedgerValidPeriods]);
    const jobseekerLedgerColumns = useMemo(() => getJobseekerLedgerColumns(jobseekerDraftEdits, updateJobseekerDraft, isEditingJobseeker, validPeriodDrafts, updateValidPeriodDraft, jobseekerEmploymentPeriodDrafts, updateJobseekerEmploymentPeriodDraft, showJobseekerValidation), [jobseekerDraftEdits, updateJobseekerDraft, isEditingJobseeker, validPeriodDrafts, updateValidPeriodDraft, jobseekerEmploymentPeriodDrafts, updateJobseekerEmploymentPeriodDraft, showJobseekerValidation]);
    const { data: feeLedgerData, isLoading: isFeeLedgerLoading } = useFeeLedgerList({ fiscalYear, page, perPage }, activeTab === LEDGER_TAB.fee);
    const { data: feePayerOptionsData } = useFeePayerOptions();
    const feePayerOptions = useMemo(() => feePayerOptionsData?.data ?? [], [feePayerOptionsData]);
    const [isEditingFee, setIsEditingFee] = useState(false);
    const [showFeeValidation, setShowFeeValidation] = useState(false);
    const [feeRows, setFeeRows] = useState<TFeeLedgerRow[]>([]);
    const [deletedFeeIds, setDeletedFeeIds] = useState<number[]>([]);
    const [isFeeDirty, setIsFeeDirty] = useState(false);
    const serverFeeRows = useMemo<TFeeLedgerRow[]>(
        () =>
            (feeLedgerData?.data ?? []).map((dto) => ({
                rowKey: nextFeeRowKey(),
                id: dto.id,
                payerCompanyId: dto.payerCompanyId,
                payerName: dto.payerName,
                collectionDate: dto.collectionDate,
                feeType: dto.feeType ?? "",
                feeAmount: dto.feeAmount != null ? String(dto.feeAmount) : "",
                feeBasis: dto.feeBasis ?? "",
                remarks: dto.remarks ?? "",
            })),
        [feeLedgerData],
    );
    useEffect(() => {
        if (!isEditingFee) {
            setFeeRows(serverFeeRows);
        }
    }, [serverFeeRows, isEditingFee]);
    const isFeeDraftInvalid = useMemo(() => feeRows.some(isFeeRowOverLimit), [feeRows]);
    const updateFeeRow: TUpdateFeeLedgerRow = useCallback((rowKey, field, value) => {
        setFeeRows((prev) => prev.map((row) => (row.rowKey === rowKey ? { ...row, [field]: value } : row)));
        setIsFeeDirty(true);
    }, []);
    const removeFeeRow = useCallback((rowKey: string) => {
        setFeeRows((prev) => {
            const target = prev.find((row) => row.rowKey === rowKey);
            if (target?.id != null) {
                setDeletedFeeIds((ids) => [...ids, target.id as number]);
            }
            return prev.filter((row) => row.rowKey !== rowKey);
        });
        setIsFeeDirty(true);
    }, []);
    const addFeeRow = useCallback(() => {
        setFeeRows((prev) => [...prev, createEmptyFeeRow()]);
        setIsFeeDirty(true);
    }, []);
    const feeLedgerColumns = useMemo(() => getFeeLedgerColumns(isEditingFee, updateFeeRow, removeFeeRow, feePayerOptions, showFeeValidation), [isEditingFee, updateFeeRow, removeFeeRow, feePayerOptions, showFeeValidation]);
    const { mutateAsync: saveFeeLedger, isPending: isSavingFee } = useSaveFeeLedger();
    const resetFeeEditing = useCallback(() => {
        setIsEditingFee(false);
        setDeletedFeeIds([]);
        setIsFeeDirty(false);
        setFeeRows(serverFeeRows);
        setShowFeeValidation(false);
    }, [serverFeeRows]);
    const handleSaveFee = useCallback(async () => {
        setShowFeeValidation(true);
        if (isFeeDraftInvalid) {
            toast.error(INPUT_MAX_LENGTH_MESSAGE(MAX_CHARACTER_INPUT_VALIDATION));
            return;
        }
        const serverRowById = new Map(serverFeeRows.map((row) => [row.id, row]));
        const rows = [
            ...feeRows
                .filter((row) => {
                    if (row.id == null) return !isFeeRowEmpty(row);
                    const server = serverRowById.get(row.id);
                    return !server || isFeeRowDirty(row, server);
                })
                .map((row) => ({
                    id: row.id,
                    payerCompanyId: row.payerCompanyId,
                    collectionDate: row.collectionDate,
                    feeType: row.feeType || null,
                    feeAmount: row.feeAmount ? Number(row.feeAmount) : null,
                    feeBasis: row.feeBasis || null,
                    remarks: row.remarks || null,
                })),
            ...deletedFeeIds.map((id) => ({ id, isDeleted: true })),
        ];
        if (!rows.length) {
            resetFeeEditing();
            return;
        }
        try {
            await saveFeeLedger({ fiscalYear, rows });
            setIsEditingFee(false);
            setDeletedFeeIds([]);
            setIsFeeDirty(false);
            toast.success(API_SUCCESS_MESSAGE);
        }
        catch {
            toast.error(API_UNKNOWN_ERROR_MESSAGE);
        }
    }, [feeRows, serverFeeRows, deletedFeeIds, isFeeDraftInvalid, fiscalYear, saveFeeLedger, resetFeeEditing]);
    const handleDownloadFee = useCallback(async () => {
        try {
            const response = await exportFeeLedgerApi({ fiscalYear });
            downloadBlobResponse(response, `手数料管理簿_${fiscalYear}年度.xlsx`);
            toast.success(DOWNLOAD_SUCCESS_MESSAGE);
        }
        catch {
            toast.error(DOWNLOAD_FAILED_MESSAGE);
        }
    }, [fiscalYear]);
    const hasUnsavedChanges = (activeTab === LEDGER_TAB.job && hasDraftEdits) || (activeTab === LEDGER_TAB.jobseeker && (hasJobseekerDraftEdits || hasValidPeriodDrafts)) || (activeTab === LEDGER_TAB.fee && isFeeDirty);
    const [pendingTab, setPendingTab] = useState<TLedgerTabValue | null>(null);
    const discardAllDrafts = useCallback(() => {
        setDraftEdits({});
        setJobEmploymentPeriodDrafts({});
        setReferralEmploymentPeriodDrafts({});
        setIsEditing(false);
        setJobseekerDraftEdits({});
        setJobseekerEmploymentPeriodDrafts({});
        setValidPeriodDrafts({});
        setIsEditingJobseeker(false);
        setShowJobseekerValidation(false);
        resetFeeEditing();
    }, [resetFeeEditing]);
    const resetPage = useCallback(() => {
        setSearchParams((prev) => {
            prev.set("page", String(START_PAGE));
            return prev;
        });
    }, [setSearchParams]);
    const isAnyEditing = isEditing || isEditingJobseeker || isEditingFee;
    const handleTabChange = useCallback((nextTab: TLedgerTabValue) => {
        if (nextTab === activeTab) return;
        // While editing (or with unsaved drafts), confirm before leaving the tab — same as the
        // leave-page guard — instead of blocking the tab switch outright.
        if (isAnyEditing || hasUnsavedChanges) {
            setPendingTab(nextTab);
            return;
        }
        setActiveTab(nextTab);
        resetPage();
    }, [activeTab, isAnyEditing, hasUnsavedChanges, resetPage]);
    const confirmTabChange = useCallback(() => {
        if (pendingTab === null) return;
        discardAllDrafts();
        setActiveTab(pendingTab);
        setPendingTab(null);
        resetPage();
    }, [pendingTab, discardAllDrafts, resetPage]);
    const handleDownload = useCallback(async () => {
        try {
            const response = await exportJobLedgerApi({ fiscalYear });
            downloadBlobResponse(response, `求人管理簿_${fiscalYear}年度.xlsx`);
            toast.success(DOWNLOAD_SUCCESS_MESSAGE);
        }
        catch {
            toast.error(DOWNLOAD_FAILED_MESSAGE);
        }
    }, [fiscalYear]);
    const handleDownloadJobseeker = useCallback(async () => {
        try {
            const response = await exportJobseekerLedgerApi({ fiscalYear });
            downloadBlobResponse(response, `求職管理簿_${fiscalYear}年度.xlsx`);
            toast.success(DOWNLOAD_SUCCESS_MESSAGE);
        }
        catch {
            toast.error(DOWNLOAD_FAILED_MESSAGE);
        }
    }, [fiscalYear]);
  return (
    <Stack gap={3} sx={{ height: "calc(100vh - 100px)", minHeight: 0 }}>
      <TopPage title="管理簿" enablePageBack={false} />

      <LedgerTabs value={activeTab} selected={activeTab} options={LEDGER_TAB_OPTIONS} onChange={(_, value) => handleTabChange(value as TLedgerTabValue)}>
        <InnerBox bordered showDivider={false} sx={{
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            "& > .MuiBox-root": { flex: 1, minHeight: 0, minWidth: 0, display: "flex", flexDirection: "column" },
        }}>
          <Stack gap={2} sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="sub16Bold" color={text.primary} sx={{ letterSpacing: 0 }}>
                {getActiveLedgerLabel(activeTab)}（{fiscalYear}年度）
              </Typography>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="cap12" color={text.secondary} sx={{ letterSpacing: 0 }}>
                  対象年度
                </Typography>
                <Select options={yearOptions} value={fiscalYear} onChange={(value) => handleFiscalYearChange(Number(value))} size="md" clearable={false} nonControl disabled={isEditing || isEditingJobseeker || isEditingFee} IconComponent={IconExpandMore} sx={{ width: "127px", height: "32px" }}/>
              </Stack>
            </Stack>

            {activeTab === LEDGER_TAB.job && (<LedgerTable columns={jobLedgerColumns} rows={jobLedgerRows} isLoading={isJobLedgerLoading} bodyRowHeight={83}/>)}
            {activeTab === LEDGER_TAB.jobseeker && (<LedgerTable columns={jobseekerLedgerColumns} rows={jobseekerLedgerRows} isLoading={isJobseekerLedgerLoading} bodyRowHeight={88}/>)}
            {activeTab === LEDGER_TAB.fee && (<Stack gap={1} sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
                <LedgerTable columns={feeLedgerColumns} rows={feeRows} isLoading={isFeeLedgerLoading} headRowHeight={79} bodyRowHeight={83}/>
                {isEditingFee && (<Box>
                  <Button variant="text" size="md" startIcon={<IconAddCircleOutline color={button.primary}/>} onClick={addFeeRow}>
                    <Typography variant="body14Semi" color={text.primary}>
                      企業を追加する
                    </Typography>
                  </Button>
                </Box>)}
              </Stack>)}

            
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
              {activeTab === LEDGER_TAB.job ? (<Pagination totalPage={jobLedgerData?.pagination?.totalPage ?? 1} disabled={isEditing}/>) : activeTab === LEDGER_TAB.jobseeker ? (<Pagination totalPage={jobseekerLedgerData?.pagination?.totalPage ?? 1} disabled={isEditingJobseeker}/>) : (<Pagination totalPage={feeLedgerData?.pagination?.totalPage ?? 1} disabled={isEditingFee}/>)}
              <Stack direction="row" gap={2}>
                <Typography
                  component="a"
                  href="https://support.iro-hana.com/3885d48302ae80dab01ac4354ea1a844"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body14Semi"
                  sx={{ alignSelf: "center", color: "#185DE3", cursor: "pointer" }}
                >
                  管理簿記載例
                </Typography>
                <Button variant="outline" size="md" startIcon={<IconFileDownload color={icon.muted}/>} onClick={activeTab === LEDGER_TAB.job ? handleDownload : activeTab === LEDGER_TAB.jobseeker ? handleDownloadJobseeker : handleDownloadFee} sx={{ borderColor: `${text.tertiary} !important`, color: text.secondary }}>
                  <Typography variant="body14Semi" color={text.secondary}>
                    {getActiveLedgerLabel(activeTab)}をダウンロード
                  </Typography>
                </Button>
                {activeTab === LEDGER_TAB.job &&
            (isEditing ? (<Button key="save" variant="primary" size="md" startIcon={<IconSave/>} onClick={handleSave} disabled={isJobDraftInvalid}>
                      <Typography variant="body14Semi">
                        保存
                      </Typography>
                    </Button>) : (<Button key="edit" variant="outline" size="md" startIcon={<IconEdit color={button.primary}/>} onClick={() => setIsEditing(true)}>
                      <Typography variant="body14Semi" color={button.primary}>
                        編集
                      </Typography>
                    </Button>))}
                {activeTab === LEDGER_TAB.jobseeker &&
            (isEditingJobseeker ? (<Button key="save" variant="primary" size="md" startIcon={<IconSave/>} onClick={handleSaveJobseeker} disabled={isJobseekerDraftInvalid}>
                      <Typography variant="body14Semi">
                        保存
                      </Typography>
                    </Button>) : (<Button key="edit" variant="outline" size="md" startIcon={<IconEdit color={button.primary}/>} onClick={() => setIsEditingJobseeker(true)}>
                      <Typography variant="body14Semi" color={button.primary}>
                        編集
                      </Typography>
                    </Button>))}
                {activeTab === LEDGER_TAB.fee &&
            (isEditingFee ? (<Button key="save" variant="primary" size="md" startIcon={<IconSave/>} onClick={handleSaveFee} disabled={isFeeDraftInvalid}>
                      <Typography variant="body14Semi">
                        保存
                      </Typography>
                    </Button>) : (<Button key="edit" variant="outline" size="md" startIcon={<IconEdit color={button.primary}/>} onClick={() => setIsEditingFee(true)}>
                      <Typography variant="body14Semi" color={button.primary}>
                        編集
                      </Typography>
                    </Button>))}
              </Stack>
            </Stack>
          </Stack>
        </InnerBox>
      </LedgerTabs>

      
      <ConfirmLeavePage shouldBlock={(hasDraftEdits && !isSaving && !isSavingJobEmploymentPeriods) || ((hasJobseekerDraftEdits || hasValidPeriodDrafts) && !isSavingJobseeker && !isSavingValidPeriods) || (isFeeDirty && !isSavingFee)}/>
      {pendingTab !== null && (<Modal isOpen onClose={() => setPendingTab(null)} onOk={confirmTabChange} labelClose="いいえ" labelOk="はい" width={500}>
          <Stack mb={2}>
            <Typography>{UNSAVED_CHANGES_CONFIRM_MESSAGE}</Typography>
          </Stack>
        </Modal>)}
    </Stack>);
};
